import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantSummary, TenantUsageResponse } from '@cloudsign/shared';
import type { TenantStatus } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatBytes, formatMonth, formatNumber } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { TenantUsersSection } from './TenantUsersSection';
import { TenantSmsSection } from './TenantSmsSection';
import { TenantClosureSection, TenantExportSection, TenantOverviewSection, TenantSupportSection, useTenantOverview } from './TenantPartnerSections';
import { tenantTone, useModuleRegistry } from './TenantsPage';

function UsageTable({ tenantId }: { tenantId: string }) {
  const q = useQuery({
    queryKey: ['platform', 'tenants', tenantId, 'usage'],
    queryFn: () => api.get(`/platform/tenants/${tenantId}/usage`, { schema: TenantUsageResponse }),
  });
  const { periods, metrics, cell } = useMemo(() => {
    const rows = q.data?.rows ?? [];
    const periods = [...new Set(rows.map((r) => r.period))].sort().reverse();
    const metrics = [...new Set(rows.map((r) => r.metric))];
    const cell = new Map(rows.map((r) => [`${r.metric}|${r.period}`, r.value]));
    return { periods, metrics, cell };
  }, [q.data]);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (periods.length === 0) return <p className="text-slate-600">{t('common.noResults')}</p>;
  return (
    <TableWrap caption={t('tenants.usage')}>
      <thead className="border-b border-slate-200 bg-slate-50">
        <tr>
          <Th>{t('tenants.month')}</Th>
          {metrics.map((m) => (
            <Th key={m}>{t(`usage.${m}`)}</Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {periods.map((p) => (
          <tr key={p} className="border-t border-slate-100 hover:bg-slate-50">
            <th scope="row" className="px-4 py-3 text-left font-normal">
              {formatMonth(p)}
            </th>
            {metrics.map((m) => {
              const v = cell.get(`${m}|${p}`);
              return (
                <td key={m} className="px-4 py-3">
                  {v === undefined ? '' : m === 'storage_bytes' ? formatBytes(v) : formatNumber(v)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const canManage = useCan('platform.tenants_manage');
  const canModules = useCan('platform.modules_manage');
  const canBilling = useCan('platform.billing_view');
  const registry = useModuleRegistry();
  const q = useQuery({ queryKey: ['platform', 'tenants', id], queryFn: () => api.get(`/platform/tenants/${id}`, { schema: TenantSummary }) });
  const overview = useTenantOverview(id);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<TenantStatus>('active');
  useEffect(() => {
    if (q.data) {
      setName(q.data.name);
      setStatus(q.data.status);
    }
  }, [q.data]);

  const update = useMutation({
    mutationFn: (body: { name?: string; status?: TenantStatus }) => api.patch(`/platform/tenants/${id}`, { body, schema: TenantSummary }),
    onSuccess: async (tn) => {
      qc.setQueryData(['platform', 'tenants', id], tn);
      await qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
  });
  const toggle = useMutation({
    mutationFn: (v: { key: string; enabled: boolean }) =>
      api.put(`/platform/tenants/${id}/modules/${v.key}`, { body: { enabled: v.enabled }, schema: TenantSummary }),
    onSuccess: (tn) => {
      qc.setQueryData(['platform', 'tenants', id], tn);
    },
  });

  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const tn = q.data;
  const optional = (registry.data ?? []).filter((m) => m.hasFeatureFlag);

  return (
    <>
      <PageHeader title={tn.name} actions={
          <span className="flex flex-wrap gap-4">
            {canBilling ? <Link className="text-brand hover:underline" to={`/platform/billing/tenants/${id}`}>{t('tenants.billingLink')}</Link> : null}
            <Link className="text-brand hover:underline" to="/platform/tenants">{t('common.back')}</Link>
          </span>
        } />
      <div className="space-y-6">
        <TenantOverviewSection tenantId={id} />
        <Card title={t('tenants.details')}>
          <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="font-medium">{t('tenants.slug')}</dt>
              <dd className="font-mono">{tn.slug}</dd>
            </div>
            <div>
              <dt className="font-medium">{t('common.status')}</dt>
              <dd>
                <Badge tone={tenantTone(tn.status)}>{t(`status.${tn.status}`)}</Badge>
              </dd>
            </div>
            <div>
              <dt className="font-medium">{t('tenants.users')}</dt>
              <dd>{tn.userCount}</dd>
            </div>
          </dl>
          <ErrorLine error={update.error} />
          {canManage ? (
            <div className="mt-3 grid max-w-md gap-4">
              <TextField label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} />
              <Button className="self-start" disabled={update.isPending || name === tn.name || !name.trim()} onClick={() => update.mutate({ name })}>
                {t('tenants.rename')}
              </Button>
              <SelectField label={t('common.status')} value={status} onChange={(e) => setStatus(e.target.value as TenantStatus)}>
                {(['active', 'suspended', 'archived'] as const).map((s) => (
                  <option key={s} value={s}>
                    {t(`status.${s}`)}
                  </option>
                ))}
              </SelectField>
              {status !== tn.status ? (
                <div className="space-y-2">
                  {status === 'suspended' ? <Alert kind="info">{t('tenants.suspendWarning')}</Alert> : null}
                  <ConfirmButton label={t('tenants.changeStatus')} pending={update.isPending} onConfirm={() => update.mutate({ status })} />
                </div>
              ) : null}
            </div>
          ) : null}
        </Card>

        <Card title={t('tenants.modules')}>
          <ErrorLine error={toggle.error} />
          {optional.length === 0 ? (
            <p className="text-slate-600">{t('tenants.noModules')}</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {optional.map((m) => (
                <CheckboxField
                  key={m.key}
                  label={t(m.nameKey)}
                  checked={tn.enabledModules.includes(m.key)}
                  disabled={!canModules || toggle.isPending}
                  onChange={(e) => toggle.mutate({ key: m.key, enabled: e.target.checked })}
                />
              ))}
            </div>
          )}
        </Card>

        <TenantSmsSection tenantId={id} />

        <TenantUsersSection tenantId={id} />

        <TenantSupportSection tenantId={id} active={tn.status === 'active' && (overview.data?.supportAccessAllowed ?? false)} />
        <TenantExportSection tenantId={id} />
        <TenantClosureSection tenantId={id} slug={tn.slug} />

        <Card title={t('tenants.usage')}>
          <UsageTable tenantId={id} />
        </Card>
      </div>
    </>
  );
}
